import { ArrowRight, UserCheck, UserCog } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import type { UserRole } from '@/types';

import TopNav from '@/components/layout/top-nav';
import { Button } from '@/components/ui/button';
import useAuth from '@/hooks/use-auth';
import useSelectRole from '@/hooks/use-select-role';
import { APP_ROUTES } from '@/lib/routes/app-routes';
import { cn } from '@/lib/utils';
import { USER_ROLES } from '@/utils/constants/roles';

const ROLE_ICONS = {
  [USER_ROLES.ops]: UserCheck,
  [USER_ROLES.ad]: UserCog,
  [USER_ROLES.qs]: UserCheck,
} as const;

const ChooseRolePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectablePermissionRoles } = useAuth();
  const { mutate: selectRole, isPending, variables: submittedRole } = useSelectRole();

  if (selectablePermissionRoles.length === 0) {
    return <Navigate to={APP_ROUTES.dashboard} replace />;
  }

  const handleSelect = (role: UserRole) => () => {
    selectRole(role, {
      onSuccess: () => navigate(APP_ROUTES.dashboard, { replace: true }),
    });
  };

  return (
    <div className="bg-train-bg flex min-h-screen flex-col">
      <TopNav visibility={false} />
      <div className="flex flex-1 flex-col items-center px-6 py-12">
        <h1 className="text-foreground text-center text-2xl font-bold">
          {t('common:chooseRole.title')}
        </h1>
        <p className="text-muted-foreground mt-2 max-w-2xl text-center text-sm">
          {t('common:chooseRole.subtitle')}
        </p>

        <div className="mt-10 grid w-full max-w-5xl gap-6 sm:grid-cols-2">
          {selectablePermissionRoles.map((role) => {
            const Icon = ROLE_ICONS[role];
            const isSubmitting = isPending && submittedRole === role;

            return (
              <div
                key={role}
                className={cn(
                  'border-border bg-card flex flex-col items-start rounded-xl border p-6 text-left transition-colors',
                  isPending && !isSubmitting && 'pointer-events-none opacity-50'
                )}
              >
                <span className="bg-muted text-muted-foreground rounded-lg p-3">
                  <Icon className="size-5" />
                </span>
                <span className="text-foreground mt-4 text-lg font-semibold">
                  {t(`common:chooseRole.roles.${role}.name`)}
                </span>
                <Button
                  variant="ghost"
                  onClick={handleSelect(role)}
                  disabled={isPending}
                  aria-label={t(`common:chooseRole.roles.${role}.name`)}
                  className="text-foreground mt-6 flex items-center gap-2 p-0 text-sm font-medium"
                >
                  {isSubmitting ? t('common:chooseRole.selecting') : t('common:chooseRole.action')}
                  <ArrowRight className="size-4" />
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ChooseRolePage;
